import { Component, inject } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import {
  TalentSearchPlansService,
  TalentPoolPlan,
  TalentPoolPlanPrice,
  transformPlans,
} from 'src/app/services/talent-search-plans.service';

interface PlanCard {
  type: 'professional' | 'executive_pro';
  nameKey: string;
  descKey: string;
  periodKey: string;
  ctaKey: string;
  highlighted: boolean;
  badgeKey: string | null;
  introKey: string | null;
  includesKey: string;
  billedNoteKey: string;
  featuresKeys: string[];
  boldIndex: number | null;
}

@Component({
  selector: 'app-ts-v2-pricing-section',
  templateUrl: './pricing-section.component.html',
  styleUrls: ['./pricing-section.component.scss'],
})
export class V2PricingSectionComponent {
  private plansService = inject(TalentSearchPlansService);
  plansQuery = this.plansService.getPlans();

  selectedPeriod = 12;
  plans: TalentPoolPlan[] = [];
  availableDurations: number[] = [];

  planCards: Record<string, PlanCard> = {
    professional: {
      type: 'professional',
      nameKey: 'talentSearchV2.pricing.plans.professional.name',
      descKey: 'talentSearchV2.pricing.plans.professional.desc',
      periodKey: 'talentSearchV2.pricing.plans.professional.period',
      ctaKey: 'talentSearchV2.pricing.plans.professional.cta',
      highlighted: false,
      badgeKey: null,
      introKey: null,
      includesKey: 'talentSearchV2.pricing.includes',
      billedNoteKey: 'talentSearchV2.pricing.plans.professional.billedNote',
      featuresKeys: [
        'talentSearchV2.pricing.plans.professional.feature1',
        'talentSearchV2.pricing.plans.professional.feature2',
        'talentSearchV2.pricing.plans.professional.feature3',
        'talentSearchV2.pricing.plans.professional.feature4'
      ],
      boldIndex: 2,
    },
    executive_pro: {
      type: 'executive_pro',
      nameKey: 'talentSearchV2.pricing.plans.executive.name',
      descKey: 'talentSearchV2.pricing.plans.executive.desc',
      periodKey: 'talentSearchV2.pricing.plans.executive.period',
      ctaKey: 'talentSearchV2.pricing.plans.executive.cta',
      highlighted: true,
      badgeKey: 'talentSearchV2.pricing.plans.executive.badge',
      introKey: 'talentSearchV2.pricing.plans.executive.intro',
      includesKey: 'talentSearchV2.pricing.plans.executive.includesLabel',
      billedNoteKey: 'talentSearchV2.pricing.plans.executive.billedNote',
      featuresKeys: [
        'talentSearchV2.pricing.plans.executive.feature1',
        'talentSearchV2.pricing.plans.executive.feature2',
        'talentSearchV2.pricing.plans.executive.feature3',
        'talentSearchV2.pricing.plans.executive.feature4'
      ],
      boldIndex: 0,
    },
  };

  constructor(
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private translate: TranslateService,
  ) {
    this.matIconRegistry.addSvgIcon(
      'logo-white',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/logo-white.svg',
      ),
    );
    this.matIconRegistry.addSvgIcon(
      'sar',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/sar.svg'),
    );

    this.plansQuery.result$.subscribe((result) => {
      if (result.isSuccess && result.data?.data) {
        const transformed = transformPlans(result.data.data);
        const isRtl = this.translate.currentLang === 'ar';
        this.plans = isRtl
          ? [transformed.executive_pro, transformed.professional]
          : [transformed.professional, transformed.executive_pro];
        if (transformed.professional.prices.length) {
          this.availableDurations = transformed.professional.prices
            .map((p) => p.duration_months)
            .sort((a, b) => a - b);
          this.selectedPeriod = this.availableDurations.includes(12) ? 12 : this.availableDurations[0];
        }
      }
    });
  }

  selectPeriod(period: number): void {
    this.selectedPeriod = period;
  }

  getDurationLabel(duration: number): string {
    switch (duration) {
      case 1:
        return 'talentSearchV2.pricing.toggle.monthly';
      case 3:
        return 'talentSearchV2.pricing.toggle.3months';
      case 12:
        return 'talentSearchV2.pricing.toggle.annual';
      default:
        return 'talentSearchV2.pricing.toggle.monthly';
    }
  }

  getCurrentPrice(plan: TalentPoolPlan): TalentPoolPlanPrice | undefined {
    return plan.prices.find((p) => p.duration_months === this.selectedPeriod);
  }

  getSavingsForDuration(duration: number): number {
    if (!this.plans.length) return 0;
    const price = this.plans[0].prices.find(
      (p) => p.duration_months === duration,
    );
    return price?.savings_percentage ?? 0;
  }

  formatPrice(value: number): string {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  getBilledNoteKey(planType: string): string {
    const base = planType === 'executive_pro'
      ? 'talentSearchV2.pricing.plans.executive'
      : 'talentSearchV2.pricing.plans.professional';
    return `${base}.billedNote${this.selectedPeriod}`;
  }

  getStartedUrl(plan: TalentPoolPlan): string {
    const subdomain = environment.server === 'dev' ? 'dev' : 'app';
    const price = this.getCurrentPrice(plan);
    return `https://${subdomain}.uptal.com/register?type=recruiter-program&feature=talent-search&plan=${plan.type}&duration=${this.selectedPeriod}${price ? '&plan_id=' + price.id : ''}`;
  }
}
